import { useEffect, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { Button, FormSection, Input, StatusBadge } from '@/components/ui';
import {
  usePhone2faRequestMutation,
  usePhone2faVerifyMutation,
} from '../hooks/useMyAccountSecurity';

const RESEND_COOLDOWN_SECONDS = 60;
const PHONE_RE = /^0\d{8,10}$/;
const CODE_RE = /^\d{6}$/;

interface PhoneTwoFactorEnrollProps {
  /** 현재 휴대폰 2FA 등록 여부 (me 응답에서 주입). */
  enrolled: boolean;
  onEnrolled?: () => void;
}

export const PhoneTwoFactorEnroll = ({ enrolled, onEnrolled }: PhoneTwoFactorEnrollProps) => {
  const { t } = useTranslation('common');
  const requestMutation = usePhone2faRequestMutation();
  const verifyMutation = usePhone2faVerifyMutation();

  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ kind: 'info' | 'success'; text: string } | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const normalizedPhone = phone.replace(/[^0-9]/g, '');

  const handleRequest = async () => {
    setPhoneError(null);
    setFeedback(null);
    if (normalizedPhone === '') {
      setPhoneError(t('admin.me.phone2fa.errPhoneRequired'));
      return;
    }
    if (!PHONE_RE.test(normalizedPhone)) {
      setPhoneError(t('admin.me.phone2fa.errPhoneInvalid'));
      return;
    }
    try {
      await requestMutation.mutateAsync({ phone: normalizedPhone });
      setCodeSent(true);
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setFeedback({ kind: 'info', text: t('admin.me.phone2fa.codeSent') });
    } catch {
      setPhoneError(t('admin.me.phone2fa.errRequestFailed'));
    }
  };

  const handleVerify = async () => {
    setCodeError(null);
    setFeedback(null);
    if (!CODE_RE.test(code)) {
      setCodeError(t('admin.me.phone2fa.errCodeRequired'));
      return;
    }
    try {
      await verifyMutation.mutateAsync({ code });
      setFeedback({ kind: 'success', text: t('admin.me.phone2fa.enrolledSuccess') });
      setCode('');
      setCodeSent(false);
      onEnrolled?.();
    } catch (err: unknown) {
      const errCode = err instanceof Error ? err.message : 'UNKNOWN';
      if (
        errCode === 'USER_PHONE_2FA_CODE_INVALID' ||
        errCode === 'USER_PHONE_2FA_CODE_EXPIRED' ||
        errCode === 'USER_2FA_INVALID'
      ) {
        setCodeError(t('admin.me.phone2fa.errCodeInvalid'));
        return;
      }
      setCodeError(t('admin.me.phone2fa.errVerifyFailed'));
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <StatusBadge
          label={
            enrolled
              ? t('admin.me.phone2fa.statusEnrolled')
              : t('admin.me.phone2fa.statusNotEnrolled')
          }
          variant={enrolled ? 'success' : 'neutral'}
          size="sm"
        />
      </div>

      <p className="text-sm" style={{ color: 'var(--content-muted)' }}>
        {t('admin.me.phone2fa.hint')}
      </p>

      <FormSection
        label={t('admin.me.phone2fa.phoneLabel')}
        density="compact"
        required
        {...(phoneError ? { error: phoneError } : {})}
      >
        <div className="flex items-center gap-2">
          <Input
            type="tel"
            inputMode="numeric"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={t('admin.me.phone2fa.phonePlaceholder')}
            maxLength={13}
            autoComplete="tel"
            aria-invalid={phoneError ? true : undefined}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void handleRequest()}
            disabled={requestMutation.isPending || cooldown > 0}
          >
            {requestMutation.isPending
              ? t('admin.me.phone2fa.requesting')
              : cooldown > 0
                ? t('admin.me.phone2fa.resendCountdown', { seconds: cooldown })
                : codeSent
                  ? t('admin.me.phone2fa.resend')
                  : t('admin.me.phone2fa.requestCode')}
          </Button>
        </div>
      </FormSection>

      {codeSent ? (
        <FormSection
          label={t('admin.me.phone2fa.codeLabel')}
          density="compact"
          required
          {...(codeError ? { error: codeError } : {})}
        >
          <div className="flex items-center gap-2">
            <Input
              type="text"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
              placeholder={t('admin.me.phone2fa.codePlaceholder')}
              maxLength={6}
              autoComplete="one-time-code"
              aria-invalid={codeError ? true : undefined}
            />
            <Button
              type="button"
              size="sm"
              onClick={() => void handleVerify()}
              disabled={verifyMutation.isPending}
            >
              {verifyMutation.isPending
                ? t('admin.me.phone2fa.verifying')
                : t('admin.me.phone2fa.verify')}
            </Button>
          </div>
        </FormSection>
      ) : null}

      {feedback ? (
        <div
          className="rounded-md p-3 text-sm"
          role={feedback.kind === 'success' ? 'status' : undefined}
          aria-live="polite"
          style={
            feedback.kind === 'success'
              ? { background: 'var(--status-success-bg)', color: 'var(--status-success-fg)' }
              : { background: 'var(--status-info-bg)', color: 'var(--status-info-fg)' }
          }
        >
          {feedback.text}
        </div>
      ) : null}
    </div>
  );
};
